import React, { useEffect, useState, useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { money, moneyK, mesLabel, hoy, Title, Empty } from './ui';

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
const tick = { fill: 'var(--chart-text)', fontSize: 11 };
const PRESETS = [
  { key: '12m', label: 'Últimos 12 meses', meses: 12 },
  { key: '24m', label: 'Últimos 2 años', meses: 24 },
  { key: 'anio', label: 'Este año' },
  { key: 'todo', label: 'Todo' },
];

export default function AgenciaFinanzas() {
  const [preset, setPreset] = useState('12m');
  const [data, setData] = useState(null);
  const [indices, setIndices] = useState({ cotizaciones: [], inflacion: [] });
  const [cot, setCot] = useState({ fecha: hoy(), oficial: '', blue: '', mep: '' });
  const [inf, setInf] = useState({ mes: hoy().slice(0, 7), porcentaje: '' });
  const [moneda, setMoneda] = useState('ars');

  const rango = useMemo(() => {
    const hasta = hoy();
    if (preset === 'todo') return { desde: '2020-01-01', hasta };
    if (preset === 'anio') return { desde: hasta.slice(0, 4) + '-01-01', hasta };
    const d = new Date(); d.setMonth(d.getMonth() - (preset === '24m' ? 24 : 12));
    return { desde: d.toISOString().slice(0, 10), hasta };
  }, [preset]);

  const cargar = () => {
    api.get('/agency/reportes', { params: rango }).then(r => setData(r.data)).catch(() => {});
    api.get('/agency/indices').then(r => setIndices(r.data)).catch(() => {});
  };
  useEffect(cargar, [rango]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const cotPorMes = Object.fromEntries((data.cotizaciones || []).map(c => [c.mes, c]));
  const inflPorMes = Object.fromEntries((data.inflacion || []).map(i => [i.mes, Number(i.porcentaje)]));
  const serie = (data.por_mes || []).map(m => {
    const c = cotPorMes[m.mes] || {};
    const tc = Number(c.oficial || c.mep || c.blue || 0);
    return {
      mes: mesLabel(m.mes), mesRaw: m.mes,
      cobrado: Number(m.cobrado), pendiente: Number(m.pendiente),
      usd: tc ? Math.round(Number(m.cobrado) / tc) : null,
      inflacion: inflPorMes[m.mes] ?? null,
    };
  });
  const egresosPorMes = Object.fromEntries((data.egresos_por_mes || []).map(e => [e.mes, Number(e.total)]));
  const serieConEgresos = serie.map(s => ({ ...s, egresos: egresosPorMes[s.mesRaw] || 0, resultado: s.cobrado - (egresosPorMes[s.mesRaw] || 0) }));
  const totalCobrado = serie.reduce((a, s) => a + s.cobrado, 0);
  const totalEgresos = serieConEgresos.reduce((a, s) => a + s.egresos, 0);
  const totalUsd = serie.reduce((a, s) => a + (s.usd || 0), 0);

  const guardarCot = async (e) => {
    e.preventDefault();
    try { await api.post('/agency/indices/cotizacion', cot); toast.success('Cotización guardada'); setCot({ ...cot, oficial: '', blue: '', mep: '' }); cargar(); }
    catch { toast.error('Error'); }
  };
  const guardarInf = async (e) => {
    e.preventDefault();
    try { await api.post('/agency/indices/inflacion', inf); toast.success('Inflación guardada'); setInf({ ...inf, porcentaje: '' }); cargar(); }
    catch { toast.error('Error'); }
  };

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const btn = (a) => ({ border: '1px solid ' + (a ? 'var(--accent)' : 'var(--border)'), background: a ? 'var(--accent)' : 'transparent', color: a ? '#fff' : 'var(--text-secondary)' });

  return (
    <div>
      <Title>Finanzas</Title>

      <div className="card" style={{ marginBottom: 16, padding: '12px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {PRESETS.map(p => <button key={p.key} className="btn btn-sm" style={btn(preset === p.key)} onClick={() => setPreset(p.key)}>{p.label}</button>)}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" style={btn(moneda === 'ars')} onClick={() => setMoneda('ars')}>En pesos</button>
          <button className="btn btn-sm" style={btn(moneda === 'usd')} onClick={() => setMoneda('usd')}>En dólares</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cobrado</div><div style={{ fontSize: 24, fontWeight: 800 }}>{moneyK(totalCobrado)}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>En dólares</div><div style={{ fontSize: 24, fontWeight: 800 }}>USD {totalUsd.toLocaleString('es-AR')}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Egresos</div><div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444' }}>{moneyK(totalEgresos)}</div></div>
        <div className="card" style={{ padding: '14px 16px' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Resultado</div><div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>{moneyK(totalCobrado - totalEgresos)}</div></div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Facturación por mes</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          {moneda === 'usd' ? 'Convertido con la cotización que cargues cada mes' : 'Cobrado y pendiente, y los egresos del período'}
        </div>
        {serie.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={serieConEgresos} margin={{ left: -6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} />
              <YAxis tick={tick} tickFormatter={moneda === 'usd' ? (v) => 'u$s' + (v / 1000).toFixed(0) + 'k' : moneyK} width={72} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [moneda === 'usd' ? 'USD ' + Number(v).toLocaleString('es-AR') : money(v), n]} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--chart-text)' }} />
              {moneda === 'usd'
                ? <Bar dataKey="usd" name="Cobrado (USD)" fill="var(--accent)" radius={[6, 6, 0, 0]} />
                : <>
                    <Bar dataKey="cobrado" name="Cobrado" fill="var(--accent)" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="pendiente" name="Pendiente" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="egresos" name="Egresos" fill="#ef4444" radius={[6, 6, 0, 0]} />
                  </>}
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty>Sin datos en el período</Empty>}
      </div>

      {serie.some(s => s.inflacion !== null) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Facturación vs. inflación</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Variación mensual de lo cobrado comparada con la inflación que cargaste</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={serie.map((s, i) => ({ ...s, variacion: i > 0 && serie[i - 1].cobrado ? Math.round(((s.cobrado / serie[i - 1].cobrado) - 1) * 1000) / 10 : null }))} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} />
              <YAxis tick={tick} tickFormatter={(v) => v + '%'} width={50} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [v + '%', n]} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--chart-text)' }} />
              <Line type="monotone" dataKey="variacion" name="Tu facturación" stroke="var(--accent)" strokeWidth={2} />
              <Line type="monotone" dataKey="inflacion" name="Inflación" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Por servicio</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{data.por_servicio.map(s => (
              <tr key={s.servicio}><td style={td}>{s.servicio}</td><td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{s.cant}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(s.total)}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Top clientes</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{data.por_cliente.slice(0, 12).map(c => (
              <tr key={c.id}><td style={td}>{c.nombre}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.total)}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Por medio de pago</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{data.por_medio.map(m => (
              <tr key={m.medio}><td style={td}>{m.medio}</td><td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{m.cant}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(m.total)}</td></tr>
            ))}</tbody>
          </table>
        </div>
        {data.por_agencia.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Por agencia</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>{data.por_agencia.map(a => (
                <tr key={a.agencia}><td style={td}>{a.agencia}</td><td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{a.clientes} clientes</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(a.total)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cargar cotización</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Se usa para ver la facturación en dólares. Queda el histórico.</div>
          <form onSubmit={guardarCot}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 8 }}>
              <div className="form-group"><label>Fecha</label><input type="date" value={cot.fecha} onChange={e => setCot({ ...cot, fecha: e.target.value })} required /></div>
              <div className="form-group"><label>Oficial</label><input type="number" step="0.01" value={cot.oficial} onChange={e => setCot({ ...cot, oficial: e.target.value })} /></div>
              <div className="form-group"><label>Blue</label><input type="number" step="0.01" value={cot.blue} onChange={e => setCot({ ...cot, blue: e.target.value })} /></div>
              <div className="form-group"><label>MEP</label><input type="number" step="0.01" value={cot.mep} onChange={e => setCot({ ...cot, mep: e.target.value })} /></div>
            </div>
            <button className="btn btn-primary btn-sm" type="submit"><Save size={14} /> Guardar</button>
          </form>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
            <thead><tr><th style={th}>Fecha</th><th style={th}>Oficial</th><th style={th}>Blue</th><th style={th}>MEP</th></tr></thead>
            <tbody>{indices.cotizaciones.slice(0, 6).map(c => (
              <tr key={c.fecha}><td style={td}>{String(c.fecha).slice(0, 10).split('-').reverse().join('/')}</td>
                <td style={td}>{c.oficial || '—'}</td><td style={td}>{c.blue || '—'}</td><td style={td}>{c.mep || '—'}</td></tr>
            ))}</tbody>
          </table>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cargar inflación</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>El dato mensual del INDEC, para comparar con tu facturación.</div>
          <form onSubmit={guardarInf}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group"><label>Mes</label><input type="month" value={inf.mes} onChange={e => setInf({ ...inf, mes: e.target.value })} required /></div>
              <div className="form-group"><label>Inflación %</label><input type="number" step="0.01" value={inf.porcentaje} onChange={e => setInf({ ...inf, porcentaje: e.target.value })} required /></div>
            </div>
            <button className="btn btn-primary btn-sm" type="submit"><Save size={14} /> Guardar</button>
          </form>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
            <tbody>{indices.inflacion.slice(0, 6).map(i => (
              <tr key={i.mes}><td style={td}>{mesLabel(i.mes)}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{i.porcentaje}%</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
